import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * A single highlight that glides to whichever child of the parent matches
 * `selector`. The parent must be `position: relative`. The first placement is
 * instant so the indicator never sweeps in from the left edge on load.
 */
export default function SlidingIndicator({
  selector,
  activeKey,
  className = "",
}: {
  selector: string;
  activeKey: string | number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const measure = () => {
      const target = parent.querySelector<HTMLElement>(selector);
      if (!target) { setBox(null); return; }
      setBox({ x: target.offsetLeft, y: target.offsetTop, w: target.offsetWidth, h: target.offsetHeight });
    };
    measure();
    const frame = requestAnimationFrame(() => setAnimate(true));
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(parent);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); };
  }, [selector, activeKey]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`sliding-indicator ${animate ? "is-animated" : ""} ${className}`}
      style={box ? { width: box.w, height: box.h, transform: `translate(${box.x}px, ${box.y}px)`, opacity: 1 } : { opacity: 0 }}
    />
  );
}
